// Partner code helpers for co-branded application links (THT-809).
//
// A carrier arriving from tachusa.com/apply/<slug> lands on
// /login?from=signup&tachRole=customer&partner=<slug>. The code has to survive
// the Auth0 round-trip (URL -> localStorage -> Auth0 `appState`, restored in
// index.tsx) and is sent as `third_party_logistics_slug` when the customer
// record is created in RoleSelect.
//
// localStorage only, through safeStorage. Unlike ETID this is not written to
// the `.tachusa.com` cookie: the landing page hands the code over on the login
// URL, so there is no cross-domain channel to read it back from.

import { safeGetItem, safeRemoveItem, safeSetItem } from './safeStorage';

export const PARTNER_CODE_STORAGE_KEY = 'tach_partner_code';

// Same rule as the backend slug: lowercase alphanumerics separated by single
// hyphens, at most 64 characters.
const PARTNER_CODE_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PARTNER_CODE_MAX_LENGTH = 64;

export const isValidPartnerCode = (
  code: string | null | undefined
): code is string =>
  typeof code === 'string' &&
  code.length <= PARTNER_CODE_MAX_LENGTH &&
  PARTNER_CODE_REGEX.test(code);

// The backend trims and lowercases the slug before looking it up
// (createCustomer.ts), so a link typed as `MyFactor` or with a stray space is
// still a real partner. Returns the canonical code, or null when the value is
// not a valid code even after that.
export const normalizePartnerCode = (
  raw: string | null | undefined
): string | null => {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toLowerCase();
  return isValidPartnerCode(code) ? code : null;
};

// Ignores anything that is not a valid code, so callers can hand it a raw
// value (a missing `appState.partner`, a tampered one) without checking first.
export const persistPartnerCode = (code: string | null | undefined) => {
  const normalized = normalizePartnerCode(code);
  if (!normalized) return;
  safeSetItem(PARTNER_CODE_STORAGE_KEY, normalized);
};

// Re-validated on read so a hand-edited localStorage value never reaches the
// POST /customers body.
export const getPartnerCode = (): string | null => {
  const code = safeGetItem(PARTNER_CODE_STORAGE_KEY);
  return isValidPartnerCode(code) ? code : null;
};

export const clearPartnerCode = () => {
  safeRemoveItem(PARTNER_CODE_STORAGE_KEY);
};
